import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { Menu, X, Sparkles, BookOpen, LogOut, User, Users, Settings, Heart } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import Logo from "@/components/brand/Logo";
import { CreditBubble } from "@/components/credits/CreditBubble";
import { MagicShop } from "@/components/shop/MagicShop";
import { useCredits } from "@/hooks/useCredits";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [shopOpen, setShopOpen] = useState(false);
  const { user, signOut } = useAuth();
  const { balance } = useCredits();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  return (
    <>
      <nav className="relative top-0 left-0 right-0 z-50 glass-nav">
        <div className="container mx-auto">
          <div className="flex items-center justify-between h-16 sm:h-20 relative">
            {/* Logo */}
            <Logo size="sm" mobileSize="md" linkTo={user ? "/dashboard" : "/"} />

            {/* Desktop Center Navigation - absolutely positioned for true centering */}
            <div className="hidden md:flex items-center absolute left-1/2 -translate-x-1/2">
              {user ? (
                <Button variant="outline" asChild className="font-medium">
                  <Link to="/dashboard" className="flex items-center gap-2">
                    <BookOpen className="w-4 h-4" />
                    Mina sagor
                  </Link>
                </Button>
              ) : (
                <div className="flex items-center gap-1">
                  <Button variant="nav" asChild>
                    <Link to="/how-it-works">Så fungerar det</Link>
                  </Button>
                  <Button variant="nav" asChild>
                    <Link to="/pricing">Priser</Link>
                  </Button>
                </div>
              )}
            </div>

            {/* Desktop CTA */}
            <div className="hidden md:flex items-center gap-3">
              {user ? (
                <>
                  {/* Credit Bubble */}
                  <CreditBubble onClick={() => setShopOpen(true)} />
                  
                  <DropdownMenu modal={false}>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        size="icon"
                        className="rounded-full w-10 h-10"
                        aria-label="Användarmeny"
                      >
                        <User className="w-5 h-5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48 bg-card border-border">
                      <DropdownMenuItem asChild>
                        <Link to="/profiles" className="cursor-pointer">
                          <Users className="w-4 h-4 mr-2" />
                          Mina barn
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/settings" className="cursor-pointer">
                          <Settings className="w-4 h-4 mr-2" />
                          Inställningar
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={handleSignOut}
                        className="cursor-pointer text-destructive focus:text-destructive"
                      >
                        <LogOut className="w-4 h-4 mr-2" />
                        Logga ut
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ) : (
                <>
                  <Button variant="ghost" asChild>
                    <Link to="/login">Logga in</Link>
                  </Button>
                  <Button variant="cta" size="default" className="text-sm sm:text-base" asChild>
                    <Link to="/create" className="flex items-center gap-2">
                      <Heart className="w-4 h-4" />
                      Skapa saga
                    </Link>
                  </Button>
                </>
              )}
            </div>

            {/* Mobile: Mina sagor icon + menu button */}
            <div className="md:hidden flex items-center gap-3">
              {user && (
                <Link
                  to="/dashboard"
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-border hover:bg-accent transition-colors text-sm font-medium"
                >
                  <BookOpen className="w-4 h-4" />
                  <span>Mina sagor</span>
                </Link>
              )}
              <button
                onClick={() => setIsOpen(!isOpen)}
                className={`p-2 transition-colors ${
                  user 
                    ? "rounded-full border border-border hover:bg-accent" 
                    : "rounded-xl hover:bg-cream-warm text-primary"
                }`}
                aria-label="Toggle menu"
              >
                {isOpen ? (
                  <X className="w-6 h-6" />
                ) : user ? (
                  <User className="w-5 h-5" />
                ) : (
                  <Menu className="w-6 h-6" />
                )}
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {isOpen && (
            <div className="md:hidden py-4 border-t border-border animate-fade-in">
              <div className="flex flex-col gap-2">
                {/* Only show landing page links for non-logged in users */}
                {!user && (
                  <>
                    <Button variant="ghost" className="justify-start" asChild>
                      <Link to="/how-it-works" onClick={() => setIsOpen(false)}>
                        Så fungerar det
                      </Link>
                    </Button>
                    <Button variant="ghost" className="justify-start" asChild>
                      <Link to="/pricing" onClick={() => setIsOpen(false)}>
                        Priser
                      </Link>
                    </Button>
                    <hr className="my-2 border-border" />
                  </>
                )}
                {user ? (
                  <>
                    {/* Mobile credit details */}
                    <div className="px-3 py-3 mb-2 rounded-lg bg-gold/5 border border-gold/20">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Heart className="w-4 h-4 text-red-500 fill-red-500" />
                          <span className="font-semibold text-foreground">{(balance?.monthlyCreditsRemaining ?? 0) + (balance?.purchasedCreditsBalance ?? 0)} sagor kvar</span>
                        </div>
                        <Button 
                          variant="cta" 
                          size="sm"
                          onClick={() => {
                            setIsOpen(false);
                            setShopOpen(true);
                          }}
                        >
                          <Sparkles className="w-4 h-4 mr-1" />
                          Fyll på
                        </Button>
                      </div>
                    </div>
                    
                    <Button variant="ghost" className="justify-start" asChild>
                      <Link to="/profiles" onClick={() => setIsOpen(false)}>
                        <Users className="w-4 h-4 mr-2" />
                        Mina barn
                      </Link>
                    </Button>
                    <Button variant="ghost" className="justify-start" asChild>
                      <Link to="/settings" onClick={() => setIsOpen(false)}>
                        <Settings className="w-4 h-4 mr-2" />
                        Inställningar
                      </Link>
                    </Button>
                    <Button
                      variant="ghost"
                      className="justify-start text-destructive hover:text-destructive"
                      onClick={() => {
                        handleSignOut();
                        setIsOpen(false);
                      }}
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Logga ut
                    </Button>
                  </>
                ) : (
                  <>
                    <Button variant="outline" className="justify-center" asChild>
                      <Link to="/login" onClick={() => setIsOpen(false)}>
                        Logga in
                      </Link>
                    </Button>
                    <Button variant="cta" className="justify-center" asChild>
                      <Link
                        to="/create"
                        onClick={() => setIsOpen(false)}
                        className="flex items-center gap-2"
                      >
                        <Sparkles className="w-4 h-4" />
                        Skapa saga
                      </Link>
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </nav>
      
      {/* Magic Shop Dialog */}
      <MagicShop open={shopOpen} onOpenChange={setShopOpen} />
    </>
  );
};

export default Navbar;